import { useLocalAudio, useDevices } from '@huddle01/react/hooks';
import { useState, useEffect } from 'react';

interface MicControlsProps {
  onMicToggle?: (isMuted: boolean) => void;
}

/**
 * Microphone controls with device selection
 */
export function MicControls({ onMicToggle }: MicControlsProps) {
  const [showDevices, setShowDevices] = useState(false);

  const { 
    stream, 
    isAudioOn,
    enableAudio,
    disableAudio,
    changeAudioSource,
  } = useLocalAudio();

  const { devices, refreshDevices, preferredDeviceId, setPreferredDevice } = useDevices({ type: 'mic' });

  // Fetch devices on mount
  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  // Get audio input devices only
  const audioDevices = devices.filter(d => d.kind === 'audioinput');

  const handleToggleMic = async () => {
    if (isAudioOn) {
      await disableAudio();
      onMicToggle?.(true);
    } else {
      await enableAudio();
      onMicToggle?.(false);
    }
  };

  const handleDeviceChange = async (deviceId: string) => {
    setPreferredDevice(deviceId);
    setShowDevices(false);
    
    // If mic is on, change to new device
    if (isAudioOn) {
      await changeAudioSource(deviceId);
    }
  };

  return (
    <div className="mic-controls">
      <button 
        className={`mic-button ${isAudioOn ? 'active' : 'muted'}`}
        onClick={handleToggleMic}
        title={isAudioOn ? 'Mute microphone' : 'Unmute microphone'}
      >
        {isAudioOn ? (
          <MicIcon />
        ) : (
          <MicOffIcon />
        )}
        <span>{isAudioOn ? 'Mute' : 'Unmute'}</span>
      </button>

      <div className="device-selector">
        <button 
          className="device-button"
          onClick={() => setShowDevices(!showDevices)}
          title="Select microphone"
        >
          <ChevronIcon />
        </button>

        {showDevices && audioDevices.length > 0 && (
          <div className="device-dropdown">
            <div className="device-dropdown-header">Select Microphone</div>
            {audioDevices.map((device) => (
              <button
                key={device.deviceId}
                className={`device-option ${preferredDeviceId === device.deviceId ? 'selected' : ''}`}
                onClick={() => handleDeviceChange(device.deviceId)}
              >
                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Audio level indicator */}
      {isAudioOn && stream && (
        <AudioLevelIndicator stream={stream} />
      )}
    </div>
  );
}

/**
 * Simple audio level indicator
 */
function AudioLevelIndicator({ stream }: { stream: MediaStream }) {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    source.connect(analyser);
    analyser.fftSize = 256;
    
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let animationId: number;
    
    const updateLevel = () => {
      if (audioContext.state === 'closed') return;
      
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setLevel(average / 255);
      
      animationId = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();

    return () => {
      cancelAnimationFrame(animationId);
      source.disconnect();
      audioContext.close();
    };
  }, [stream]);

  return (
    <div className="audio-level">
      <div 
        className="audio-level-bar" 
        style={{ width: `${level * 100}%` }}
      />
    </div>
  );
}

// Simple SVG Icons
function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73l-9-9L4.27 3z"/>
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M7 10l5 5 5-5z"/>
    </svg>
  );
}
